// import lightBaseTheme from '@material-ui/core/styles/baseThemes/lightBaseTheme';
import CssBaseline from '@material-ui/core/CssBaseline/CssBaseline';
import Snackbar from '@material-ui/core/Snackbar/Snackbar';
import { createMuiTheme, MuiThemeProvider } from '@material-ui/core/styles';
import MuiPickersUtilsProvider from 'material-ui-pickers/MuiPickersUtilsProvider';
import moment from 'moment';
import 'moment/locale/en-nz';
import * as React from 'react';
import { hot } from 'react-hot-loader';
import { BrowserRouter as Router } from 'react-router-dom';
import './App.css';
import { Routes } from './Routes';
import { callApi } from './api';
import { TokenServiceClient } from './model/autogen/TokenController';
import { IAuthContextState, getToken, parseJwt, getRefreshToken, setToken, AuthContext } from './auth-context';
import { ISnackbarContextState, SnackbarContext } from './snackbar-context';
import { UserLevel } from './model/autogen/UserLevel';
import { ClaimStrings } from './model/autogen/ClaimStrings';
import { TokenModel } from './model/autogen/TokenModel';
import { OurCustomUtils } from './components/date-display-format';

moment.locale('en-nz');

const labourRed = "#CF2916";

const theme = createMuiTheme({
    palette: {
        secondary: {
            main: labourRed
        }
    },
    overrides: {
        MuiAppBar: {
            colorPrimary: {
                backgroundColor: labourRed,
                color: 'white !important'
            }
        },
        MuiButton: {
            raisedSecondary: {
                backgroundColor: labourRed
            }
        }
    }
});

class App extends React.Component<{}, IAuthContextState & ISnackbarContextState> {
    private loginRecoveryAction: () => void;
    constructor(props: {}) {
        super(props);

        const token = getToken();
        const userDetails = this.getDetailsFromToken(token);

        this.state = {
            token: token,
            user: userDetails,
            loading: false,
            snackbarMessage: "",
            isSnackbarOpen: false
        };
    }
    private getUserInfo = () => {
        const token = getToken();
        if (token)
            return parseJwt(token);
        return undefined;
    }
    private getDetailsFromToken(token?: string) {
        let userDetails = {
            level: UserLevel.Disabled,
            id: null
        };
        if (token) {
            const parsed = parseJwt(token);
            if (parsed) {
                userDetails.level = parseInt(parsed[ClaimStrings.UserLevel], 10);
                userDetails.id = parsed[ClaimStrings.UserID];
            }
        }
        return userDetails;
    }

    private setLevel = (level: UserLevel) => {
        this.setState({ user: { ...this.state.user, level } });
    }
    private setToken = (model: TokenModel | undefined) => {
        if (!model) {
            const t = getToken();
            const rt = getRefreshToken();
            if (t && rt) {
                //Invalidate this server-side before clearing
                TokenServiceClient.logout({ token: t, refreshToken: rt });
            }
        }
        setToken(model);

        if (!model) {
            this.setState({
                token: undefined
            });
            return;
        }

        this.setState({
            token: model.token,
            user: this.getDetailsFromToken(model.token)
        });
    }
    private invokeApi = async (serviceMethod: (fetcher: (url: string, init: RequestInit) => Promise<Response>) => Promise<any>) => {

        const self = this;

        try {
            return await callApi(getToken, serviceMethod,
                async (success) => {
                    const token = getToken();
                    const refreshToken = getRefreshToken();
                    // attempt to refresh the token
                    if (token && refreshToken) {
                        this.setState({ loading: true });
                        try {
                            const refreshResult = await TokenServiceClient.refresh({ token, refreshToken });
                            setToken(refreshResult);
                            this.setState({ token: refreshResult.token });

                            success();
                            return;
                        } catch {
                            console.log('Refresh failed, falling back to login');
                        }
                        this.setState({ loading: false });
                    }

                    self.setState({
                        token: undefined
                    });
                }, (success) => {
                    self.loginRecoveryAction = success;
                });
        } catch (err) {
            if (err.statusCode == 403) {
                this.showSnackbarMessage("The request you attempted to perform was denied.")
            }
            else if (err.statusCode == 500) {
                // our thing
                this.showSnackbarMessage("A server error occurred processing your request")
            } else if (typeof (err.statusCode) == 'undefined') { // weird fetch TypeError nonsense
                this.showSnackbarMessage("A communication error occurred attempting to send your request")
            }
            throw err;
        }
    }
    private doLogin = (username: string, password: string, onError: (err: any) => void, onSuccess: () => void) => {
        TokenServiceClient.login({ username, password }).then(r => {
            if (r.token) {
                this.setToken(r);
                this.showSnackbarMessage("Login successful");
                onSuccess();
            } else {
                this.showSnackbarMessage("Login denied");
            }
        }).catch(err => {
            //if (err.statusCode == 401) {  
            //}
            onError(err);
        });
    }
    private initLogin = (username: string, password: string, onError: (err: any) => void, onSuccess: () => void) => {
        this.doLogin(username, password, onError, onSuccess);
    }
    private recoveryLogin = (username: string, password: string, onError: (err: any) => void) => {
        this.doLogin(username, password, onError, this.loginRecoveryAction);
    }
    private showSnackbarMessage = (message: string) => {
        this.setState({ snackbarMessage: message, isSnackbarOpen: true });
    }
    private onCloseSnackbar = (e: any) => {
        this.setState({ snackbarMessage: "", isSnackbarOpen: false });
    }
    componentDidMount() {
        (window as any).setToken = this.setToken;
    }
    public render() {
        return (
            <>
                <CssBaseline />
                <MuiThemeProvider theme={theme}>
                    <MuiPickersUtilsProvider utils={OurCustomUtils}>
                        <AuthContext.Provider value={{
                            state: this.state,
                            methods: {
                                setToken: this.setToken,
                                invokeApi: this.invokeApi,
                                initLogin: this.initLogin,
                                getUserInfo: this.getUserInfo,
                                recoveryLogin: this.recoveryLogin,
                                setLevel: this.setLevel
                            }
                        }}>
                            <SnackbarContext.Provider value={{
                                state: this.state,
                                methods: {
                                    showMessage: this.showSnackbarMessage
                                }
                            }}>
                                <Router>
                                    <Routes />
                                </Router>
                            </SnackbarContext.Provider>
                        </AuthContext.Provider>
                        <Snackbar
                            open={this.state.isSnackbarOpen}
                            message={<>{this.state.snackbarMessage}</>}
                            autoHideDuration={4000}
                            onClose={this.onCloseSnackbar}
                        />
                    </MuiPickersUtilsProvider>
                </MuiThemeProvider>
            </>
        );
    }
}

export default hot(module)(App)


// WEBPACK FOOTER //
// ./src/App.tsx